/**
 * API Performance Monitor
 * 
 * Utility to track and analyze API performance issues,
 * with special focus on slow endpoints like /api/companies/fetchcompanies
 */
import { performanceMonitor } from './performanceMonitor';

// Configure thresholds for API performance warning levels
const PERFORMANCE_THRESHOLDS = {
  WARNING: 1000,    // 1 second - warning level
  CRITICAL: 5000,   // 5 seconds - critical level
  EXTREME: 30000    // 30 seconds - extreme performance issue
};

// Special endpoints to monitor closely
const WATCHED_ENDPOINTS = [
  '/api/companies/fetchcompanies',
  '/api/jobs/fetchjobs'
];

const apiPerformanceMonitor = {
  /**
   * Initialize the API performance monitoring system
   */
  initialize() {
    // Since performanceMonitor doesn't have listeners, we'll set up our own monitoring
    // We'll check metrics periodically instead of using events
    this.checkInterval = setInterval(() => {
      this.checkApiMetrics();
    }, 10000); // Check every 10 seconds
    
    // Log initialization
    console.log('🔍 API Performance monitoring initialized');
    
    return () => {
      // Clean up the interval when done
      if (this.checkInterval) {
        clearInterval(this.checkInterval);
        this.checkInterval = null;
      }
    };
  },
  
  /**
   * Check API performance metrics from the performance monitor
   */
  checkApiMetrics() {
    try {
      // Simple safe check for metrics
      if (!performanceMonitor || typeof performanceMonitor.getMetrics !== 'function') {
        return;
      }
      
      const allMetrics = performanceMonitor.getMetrics();
      
      // Simple check - if no api metrics or not an object, nothing to do
      if (!allMetrics || !allMetrics.api || typeof allMetrics.api !== 'object') {
        return;
      }
      
      // Iterate through api metrics safely
      for (const name in allMetrics.api) {
        if (Object.prototype.hasOwnProperty.call(allMetrics.api, name)) {
          const metric = allMetrics.api[name];
          
          // Simple check for valid metric
          if (metric && typeof metric === 'object' && typeof metric.duration === 'number') {
            this.analyzeApiMetric({
              name: name,
              duration: metric.duration
            });
          }
        }
      }
    } catch (error) {
      console.error('Error checking API metrics:', error);
    }
  },

  /**
   * Analyze a single API performance metric
   * @param {Object} metric - The performance metric object
   */
  analyzeApiMetric(metric) {
    try {
      // Basic validation
      if (!metric || !metric.name || typeof metric.duration !== 'number') {
        return;
      }
      
      const name = metric.name;
      const duration = metric.duration;
      
      // Extract endpoint from name (format is typically "GET /api/endpoint")
      const parts = name.split(' ');
      const endpoint = parts.length > 1 ? parts[1] : name;
      
      // Determine severity level based on duration
      let severity = 'info';
      if (duration > PERFORMANCE_THRESHOLDS.EXTREME) {
        severity = 'extreme';
      } else if (duration > PERFORMANCE_THRESHOLDS.CRITICAL) {
        severity = 'critical';
      } else if (duration > PERFORMANCE_THRESHOLDS.WARNING) {
        severity = 'warning';
      }
      
      // Log based on severity
      switch(severity) {
        case 'extreme':
          console.error(`⚠️ EXTREME PERFORMANCE ISSUE: ${name} took ${(duration/1000).toFixed(2)}s`);
          this.reportPerformanceIssue(endpoint, duration);
          break;
        case 'critical':
          console.error(`🔴 Critical: ${name} took ${(duration/1000).toFixed(2)}s`);
          break;
        case 'warning':
          console.warn(`🟠 Slow: ${name} took ${(duration/1000).toFixed(2)}s`);
          break;
        default:
          // Only log watched endpoints at info level to reduce noise
          if (WATCHED_ENDPOINTS.some(e => endpoint.includes(e))) {
            console.log(`🟢 API: ${name} took ${(duration/1000).toFixed(2)}s`);
          }
      }
    } catch (error) {
      console.error('Error analyzing API metric:', error);
    }
  },
  
  /**
   * Report a serious performance issue for further analysis
   * @param {string} endpoint - The API endpoint with issues
   * @param {number} duration - The duration in ms
   */
  reportPerformanceIssue(endpoint, duration) {
    // This could send data to an analytics service, error tracking, etc.
    // For now we'll just log it prominently
    
    console.group('📊 Performance Issue Report');
    console.error(`Endpoint: ${endpoint}`);
    console.error(`Duration: ${(duration/1000).toFixed(2)}s`);
    console.error(`Timestamp: ${new Date().toISOString()}`);
    console.error('Suggested actions:');
    console.error('1. Check server logs for this endpoint');
    console.error('2. Consider implementing more aggressive caching');
    console.error('3. Look for N+1 queries or missing indexes in the database');
    console.error('4. Consider pagination or data limiting if appropriate');
    console.groupEnd();
    
    // Additional action suggestions based on specific endpoints
    if (endpoint.includes('/api/companies/fetchcompanies')) {
      console.group('🔧 Specific Recommendations for /api/companies/fetchcompanies');
      console.log('1. Ensure companyDataManager is being used for all company data needs');
      console.log('2. Consider implementing server-side pagination');
      console.log('3. Check if all company data is truly needed or if it can be filtered server-side');
      console.log('4. Evaluate database indexes for company queries');
      console.groupEnd();
    }
  },
  
  /**
   * Get performance statistics for APIs
   * @returns {Object} Performance statistics
   */
  getApiStats() {
    try {
      if (!performanceMonitor || typeof performanceMonitor.getMetrics !== 'function') {
        return [];
      }
      
      const metrics = performanceMonitor.getMetrics();
      if (!metrics || !metrics.api) {
        return [];
      }
      
      const apiMetrics = metrics.api;
      const results = [];
      
      // Simpler approach without using Object.entries or forEach
      for (const name in apiMetrics) {
        if (Object.prototype.hasOwnProperty.call(apiMetrics, name)) {
          const metric = apiMetrics[name];
          if (metric && typeof metric === 'object') {
            const parts = name.split(' ');
            const endpoint = parts.length > 1 ? parts[1] : name;
            const duration = typeof metric.duration === 'number' ? metric.duration : 0;
            const count = typeof metric.count === 'number' ? metric.count : 1;
            
            results.push({
              endpoint,
              calls: count,
              avgDuration: duration / count,
              maxDuration: duration,
              minDuration: duration
            });
          }
        }
      }
      
      // Simple sort by duration
      return results.sort(function(a, b) {
        return b.avgDuration - a.avgDuration;
      });
    } catch (error) {
      console.error('Error analyzing API stats:', error);
      return [];
    }
  },
  
  /**
   * Log API performance statistics to console
   */
  logApiPerformanceReport() {
    try {
      const stats = this.getApiStats();
      
      console.group('📊 API Performance Report');
      if (stats && stats.length > 0) {
        // Use a simple for loop instead of map
        const tableData = [];
        for (let i = 0; i < stats.length; i++) {
          const s = stats[i];
          tableData.push({
            endpoint: s.endpoint,
            calls: s.calls,
            avgDuration: (s.avgDuration/1000).toFixed(2) + 's',
            maxDuration: (s.maxDuration/1000).toFixed(2) + 's'
          });
        }
        console.table(tableData);
        
        // Simple check for slow endpoints
        const slowEndpoints = [];
        for (let i = 0; i < stats.length; i++) {
          if (stats[i].avgDuration > PERFORMANCE_THRESHOLDS.WARNING) {
            slowEndpoints.push(stats[i]);
          }
        }
        
        if (slowEndpoints.length > 0) {
          console.group('🐢 Slow API Endpoints');
          for (let i = 0; i < slowEndpoints.length; i++) {
            const s = slowEndpoints[i];
            console.warn(s.endpoint + ': avg ' + (s.avgDuration/1000).toFixed(2) + 's, max ' + (s.maxDuration/1000).toFixed(2) + 's');
          }
          console.groupEnd();
        }
      } else {
        console.log('No API metrics available yet');
      }
      console.groupEnd();
    } catch (error) {
      console.error('Error logging API performance report:', error);
    }
  }
};

export default apiPerformanceMonitor;